import React, { useState } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  FileText,
  Download,
  Loader,
  CheckCircle,
  AlertCircle,
  ClipboardCheck,
  ShoppingCart,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  Settings,
  Package,
  DollarSign,
  Archive,
  FileBarChart,
  Eye,
  Users,
  Activity,
  Shield,
  Server,
} from 'lucide-react';

const Reports = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Report configuration state
  const [reportConfig, setReportConfig] = useState({
    report_title: '',
    sections: {
      audit: {
        enabled: true,
        page_types: ['homepage'],
        include_scores: true,
        include_issues: true,
        include_performance: true,
      },
      checkout: {
        enabled: true,
        include_summary: true,
        include_configuration: true,
      },
      abandoned_carts: {
        enabled: true,
        date_range: '7d',
        include_summary: true,
        include_top_products: true,
        include_trend: true,
        include_cart_list: true,
        cart_list_limit: 10,
      },
      orders: {
        enabled: true,
        date_range: '7d',
      },
      products: {
        enabled: true,
        date_range: '7d',
      },
      stock: {
        enabled: true,
      },
      content: {
        enabled: true,
      },
      pageviews: {
        enabled: true,
      },
      users: {
        enabled: true,
      },
      uptime: {
        enabled: true,
      },
      ssl: {
        enabled: true,
      },
      system: {
        enabled: true,
      },
    },
    options: {
      include_cover_page: true,
      include_table_of_contents: false,
      include_timestamps: true,
    },
  });

  const [expandedSections, setExpandedSections] = useState({
    audit: true,
    checkout: false,
    abandoned_carts: false,
    orders: false,
    products: false,
    stock: false,
    content: false,
    pageviews: false,
    users: false,
    uptime: false,
    ssl: false,
    system: false,
  });

  // Toggle section enabled/disabled
  const toggleSection = (section) => {
    setReportConfig((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: {
          ...prev.sections[section],
          enabled: !prev.sections[section].enabled,
        },
      },
    }));
  };

  // Update section option
  const updateSectionOption = (section, option, value) => {
    setReportConfig((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: {
          ...prev.sections[section],
          [option]: value,
        },
      },
    }));
  };

  // Toggle page type for audit section
  const togglePageType = (pageType) => {
    setReportConfig((prev) => {
      const currentTypes = prev.sections.audit.page_types;
      const newTypes = currentTypes.includes(pageType)
        ? currentTypes.filter((t) => t !== pageType)
        : [...currentTypes, pageType];
      return {
        ...prev,
        sections: {
          ...prev.sections,
          audit: {
            ...prev.sections.audit,
            page_types: newTypes,
          },
        },
      };
    });
  };

  // Update report options
  const updateOption = (option, value) => {
    setReportConfig((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        [option]: value,
      },
    }));
  };

  // Toggle expanded state for section
  const toggleExpanded = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Generate report
  const generateReport = async () => {
    if (!selectedSite) {
      setError('Please select a site first');
      return;
    }

    // Check if at least one section is enabled
    const enabledSections = Object.values(reportConfig.sections).filter((s) => s.enabled);
    if (enabledSections.length === 0) {
      setError('Please enable at least one section for the report');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('/app/api/reports/generate.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          site_id: selectedSite.id,
          report_title: reportConfig.report_title || `${selectedSite.name} Analytics Report`,
          sections: reportConfig.sections,
          options: reportConfig.options,
        }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Failed to generate report');
      }

      // Handle PDF download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedSite.name.replace(/[^a-zA-Z0-9_-]/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const pageTypes = [
    { key: 'homepage', label: 'Homepage' },
    { key: 'category', label: 'Category Page' },
    { key: 'product', label: 'Product Page' },
    { key: 'cms', label: 'CMS Page' },
    { key: 'blog', label: 'Blog Post' },
  ];

  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '14d', label: 'Last 14 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '60d', label: 'Last 60 days' },
    { value: '90d', label: 'Last 90 days' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">PDF Reports</h1>
          </div>
          <p className="text-gray-600">
            Generate customizable PDF reports for your Magento store analytics. Select the sections
            you want to include and download a comprehensive report.
          </p>
        </div>

        {/* Site Selection Warning */}
        {!selectedSite && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">No site selected</p>
              <p className="text-sm text-amber-700">
                Please select a site from the dropdown in the header to generate a report.
              </p>
            </div>
          </div>
        )}

        {/* Report Title */}
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Report Title</label>
          <input
            type="text"
            value={reportConfig.report_title}
            onChange={(e) =>
              setReportConfig((prev) => ({ ...prev, report_title: e.target.value }))
            }
            placeholder={selectedSite ? `${selectedSite.name} Analytics Report` : 'Analytics Report'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Section Cards */}
        <div className="space-y-4 mb-6">
          {/* Audit Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleExpanded('audit')}
            >
              <div className="flex items-center gap-3">
                <ClipboardCheck className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">SEO Audit Data</h3>
                  <p className="text-sm text-gray-500">Scores, issues, and performance metrics</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label
                  className="relative inline-flex items-center cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={reportConfig.sections.audit.enabled}
                    onChange={() => toggleSection('audit')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                {expandedSections.audit ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>

            {expandedSections.audit && reportConfig.sections.audit.enabled && (
              <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                {/* Page Types */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Types to Include
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {pageTypes.map((type) => (
                      <button
                        key={type.key}
                        onClick={() => togglePageType(type.key)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                          reportConfig.sections.audit.page_types.includes(type.key)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Include options */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.sections.audit.include_scores}
                      onChange={(e) => updateSectionOption('audit', 'include_scores', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Include scores</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.sections.audit.include_issues}
                      onChange={(e) => updateSectionOption('audit', 'include_issues', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Include issues list</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.sections.audit.include_performance}
                      onChange={(e) =>
                        updateSectionOption('audit', 'include_performance', e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Include performance</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Checkout Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleExpanded('checkout')}
            >
              <div className="flex items-center gap-3">
                <ShoppingCart className="h-6 w-6 text-green-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Checkout Analysis</h3>
                  <p className="text-sm text-gray-500">Guest checkout status and cart summary</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label
                  className="relative inline-flex items-center cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={reportConfig.sections.checkout.enabled}
                    onChange={() => toggleSection('checkout')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                {expandedSections.checkout ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>

            {expandedSections.checkout && reportConfig.sections.checkout.enabled && (
              <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.sections.checkout.include_summary}
                      onChange={(e) =>
                        updateSectionOption('checkout', 'include_summary', e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Include cart summary</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.sections.checkout.include_configuration}
                      onChange={(e) =>
                        updateSectionOption('checkout', 'include_configuration', e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Include configuration</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Abandoned Carts Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleExpanded('abandoned_carts')}
            >
              <div className="flex items-center gap-3">
                <TrendingDown className="h-6 w-6 text-red-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Abandoned Carts</h3>
                  <p className="text-sm text-gray-500">Cart abandonment analysis and trends</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label
                  className="relative inline-flex items-center cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={reportConfig.sections.abandoned_carts.enabled}
                    onChange={() => toggleSection('abandoned_carts')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                {expandedSections.abandoned_carts ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>

            {expandedSections.abandoned_carts && reportConfig.sections.abandoned_carts.enabled && (
              <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                {/* Date Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select
                    value={reportConfig.sections.abandoned_carts.date_range}
                    onChange={(e) =>
                      updateSectionOption('abandoned_carts', 'date_range', e.target.value)
                    }
                    className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {dateRangeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Include options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.sections.abandoned_carts.include_summary}
                      onChange={(e) =>
                        updateSectionOption('abandoned_carts', 'include_summary', e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Include summary stats</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.sections.abandoned_carts.include_cart_list}
                      onChange={(e) =>
                        updateSectionOption('abandoned_carts', 'include_cart_list', e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Include cart list</span>
                  </label>
                </div>

                {/* Cart list limit */}
                {reportConfig.sections.abandoned_carts.include_cart_list && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max carts to show
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      value={reportConfig.sections.abandoned_carts.cart_list_limit}
                      onChange={(e) =>
                        updateSectionOption(
                          'abandoned_carts',
                          'cart_list_limit',
                          parseInt(e.target.value) || 20
                        )
                      }
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Orders Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleExpanded('orders')}
            >
              <div className="flex items-center gap-3">
                <DollarSign className="h-6 w-6 text-emerald-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Orders Overview</h3>
                  <p className="text-sm text-gray-500">Order counts, revenue, and status breakdown</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label
                  className="relative inline-flex items-center cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={reportConfig.sections.orders.enabled}
                    onChange={() => toggleSection('orders')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                {expandedSections.orders ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>

            {expandedSections.orders && reportConfig.sections.orders.enabled && (
              <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select
                    value={reportConfig.sections.orders.date_range}
                    onChange={(e) => updateSectionOption('orders', 'date_range', e.target.value)}
                    className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {dateRangeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Product Performance Section */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleExpanded('products')}
            >
              <div className="flex items-center gap-3">
                <Package className="h-6 w-6 text-purple-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Product Performance</h3>
                  <p className="text-sm text-gray-500">Top selling products by revenue</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label
                  className="relative inline-flex items-center cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={reportConfig.sections.products.enabled}
                    onChange={() => toggleSection('products')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                {expandedSections.products ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>

            {expandedSections.products && reportConfig.sections.products.enabled && (
              <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select
                    value={reportConfig.sections.products.date_range}
                    onChange={(e) => updateSectionOption('products', 'date_range', e.target.value)}
                    className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {dateRangeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Stock Status Section - No settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Archive className="h-6 w-6 text-amber-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Stock Status</h3>
                  <p className="text-sm text-gray-500">Inventory overview and low stock alerts</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reportConfig.sections.stock.enabled}
                  onChange={() => toggleSection('stock')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Content Insights Section - No settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <FileBarChart className="h-6 w-6 text-indigo-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Content Insights</h3>
                  <p className="text-sm text-gray-500">Product content quality metrics</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reportConfig.sections.content.enabled}
                  onChange={() => toggleSection('content')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Page Views Section - No settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Eye className="h-6 w-6 text-cyan-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Page Views</h3>
                  <p className="text-sm text-gray-500">Last 7 days visitor statistics</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reportConfig.sections.pageviews.enabled}
                  onChange={() => toggleSection('pageviews')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* User Analysis Section - No settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">User Analysis</h3>
                  <p className="text-sm text-gray-500">Customer statistics and engagement</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reportConfig.sections.users.enabled}
                  onChange={() => toggleSection('users')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Uptime Monitoring Section - No settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Activity className="h-6 w-6 text-green-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">Uptime Monitoring</h3>
                  <p className="text-sm text-gray-500">Availability and response time metrics</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reportConfig.sections.uptime.enabled}
                  onChange={() => toggleSection('uptime')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* SSL Monitor Section - No settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-teal-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">SSL Monitor</h3>
                  <p className="text-sm text-gray-500">Certificate status and security headers</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reportConfig.sections.ssl.enabled}
                  onChange={() => toggleSection('ssl')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* System Information Section - No settings */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Server className="h-6 w-6 text-slate-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">System Information</h3>
                  <p className="text-sm text-gray-500">Store structure and module counts</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reportConfig.sections.system.enabled}
                  onChange={() => toggleSection('system')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Report Options */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Report Options</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={reportConfig.options.include_cover_page}
                onChange={(e) => updateOption('include_cover_page', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Include cover page</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={reportConfig.options.include_timestamps}
                onChange={(e) => updateOption('include_timestamps', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Include timestamps</span>
            </label>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-green-800">Success!</p>
              <p className="text-sm text-green-700">
                Your PDF report has been generated and downloaded.
              </p>
            </div>
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={generateReport}
          disabled={loading || !selectedSite}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg text-white font-semibold text-lg transition ${
            loading || !selectedSite
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? (
            <>
              <Loader className="h-5 w-5 animate-spin" />
              Generating Report...
            </>
          ) : (
            <>
              <Download className="h-5 w-5" />
              Generate & Download PDF
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Reports;
